import { Routes, Route, Navigate, useLocation, useParams } from "react-router";
import { useSyncExternalStore, type ReactNode } from "react";
import { ChangelogPage } from "@/pages/ChangelogPage";
import { ClientsLayout } from "@/v2/clients/ClientsLayout";
import { LoginPage } from "@/pages/LoginPage";
import { PolicyPage } from "@/pages/PolicyPage";
import { NotAuthorisedPage } from "@/pages/NotAuthorisedPage";
import { ConsentGate } from "@/components/consent/ConsentGate";
import { useAuth } from "@/contexts/AuthContext";
import { LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertMark, NoticePanel } from "@/components/ui/ErrorPanel";
import { IconTile } from "@/components/ui/IconTile";
import { accessDenial, subscribeAccessDenied } from "@/lib/access-denied";
import { V2Routes } from "@/v2/routes";

/**
 * The API did not answer who is signed in. Nobody is signed out on the
 * strength of that: the attorney is asked to try again, not to sign in.
 */
function ServerOutOfReach({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      {/* No Report: before sign-in no feedback form can open. */}
      <NoticePanel
        mark={<AlertMark />}
        role="alert"
        title="Patent Studio couldn't reach the server"
        reason="Try again in a moment. If your connection is working, the fault is on our side."
      >
        <Button onClick={onRetry}>Try again</Button>
      </NoticePanel>
    </div>
  );
}

/**
 * The session ended while the attorney worked: said so, rather than a silent
 * trip to the sign-in page, and signing in again returns to the same page.
 */
function SessionEnded({ onSignIn }: { onSignIn: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <NoticePanel
        role="alert"
        title="Your session ended"
        reason="Sign in again. What you saved is kept."
        mark={
          <IconTile size="md" shape="round">
            <LogIn />
          </IconTile>
        }
      >
        <Button onClick={onSignIn}>Sign in</Button>
      </NoticePanel>
    </div>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const {
    isAuthenticated,
    isLoading,
    unreachable,
    sessionEnded,
    retry,
    login,
  } = useAuth();
  const location = useLocation();
  const denial = useSyncExternalStore(subscribeAccessDenied, accessDenial);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (unreachable) {
    return <ServerOutOfReach onRetry={retry} />;
  }

  if (sessionEnded) {
    return (
      <SessionEnded
        onSignIn={() => {
          login(`${location.pathname}${location.search}`);
        }}
      />
    );
  }

  if (!isAuthenticated) {
    // Carry the page that was asked for, so signing in returns there rather
    // than dumping everyone on the dashboard. The sign-in page cannot read it
    // off the address bar, since by then the address bar says `/login`.
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  // Signed in, but the API has refused this account. Shown ahead of the legal
  // gate because there is nothing to consent to for someone who cannot use
  // the application, and because the gate's own request would be refused too.
  if (denial) {
    return <NotAuthorisedPage denial={denial} />;
  }

  // The one node every authenticated route passes through, so the legal gate is
  // mounted once here rather than in each layout. It sits inside the
  // authenticated branch because there is nothing to gate, and nobody to record
  // it against, until someone is signed in.
  return <ConsentGate>{children}</ConsentGate>;
}

/**
 * Sends the addresses the workflow used to answer on to the ones it answers on
 * now. Bookmarks, pasted links and anything already open in a tab all carry the
 * old `/v2` prefix, and there is no server-side rewrite to catch them.
 */
function V2PrefixRedirect() {
  const { "*": rest } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/${rest ?? ""}${search}${hash}`} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Public, and deliberately outside ProtectedRoute: the consent gate
          blocks everything inside it, so a link from the dialog to a gated page
          would dead-end. Being public also lets a policy be linked from the
          login page or an email. It wears the client pages' header, whose
          header needs no signed-in state. */}
      <Route path="/policies/:policyId" element={<ClientsLayout />}>
        <Route index element={<PolicyPage />} />
      </Route>
      {/* Signed in, unlike the two above: the notes describe a product only an
          admitted account can use, and they were behind sign-in when the v1
          layout hosted them. Its own top-level address rather than a stage, so
          a link to it opens the notes and not somebody's draft. It wears the
          client pages' header, which is what an attorney leaves it by. */}
      <Route
        path="/changelog"
        element={
          <ProtectedRoute>
            <ClientsLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ChangelogPage />} />
      </Route>
      <Route path="/v2/*" element={<V2PrefixRedirect />} />
      {/* The attorney workflow, and the reason the splat is last: it answers on
          everything the static entries above do not claim. Its stage routes are
          declared inside the subtree rather than here. */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <V2Routes />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
